import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ScaleIcon } from '@heroicons/react/24/outline';
import { academyAPI } from '../../utils/api';

const STUDENT_ROLES = ['applicant', 'respondent', 'plaintiff', 'defendant', 'prosecution', 'defence'];

const StatusBadge = ({ status }) => {
  const map = {
    completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    awaiting_student: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    awaiting_ai: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    scoring: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    setup: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    abandoned: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${map[status] || map.setup}`}>
      {String(status || '').replace(/_/g, ' ')}
    </span>
  );
};

const quotaMessage = (err) =>
  err?.response?.data?.detail || err?.response?.data?.message || 'Could not start the trial. Please try again.';

const MockTrialLobby = () => {
  const navigate = useNavigate();
  const [trials, setTrials] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [starting, setStarting] = useState(false);

  const [form, setForm] = useState({ topic_id: '', student_role: 'applicant', with_witness: false });

  const load = useCallback(async () => {
    setLoading(true);
    const [t, a] = await Promise.allSettled([academyAPI.getTrials(), academyAPI.getAreas()]);
    if (t.status === 'fulfilled') setTrials(t.value.data || []);
    if (a.status === 'fulfilled') setAreas(a.value.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Flatten topics across areas for the picker.
  const topics = areas.flatMap((area) =>
    (area.topics || []).map((t) => ({ id: t.id, label: `${area.name} — ${t.name}` }))
  );

  const handleStart = async (e) => {
    e.preventDefault();
    if (!form.topic_id || starting) return;
    setNotice('');
    setStarting(true);
    try {
      const res = await academyAPI.startTrial({
        topic_id: Number(form.topic_id),
        student_role: form.student_role,
        with_witness: form.with_witness,
      });
      const trial = res.data;
      if (trial?.id) navigate(`/student/mock-trials/${trial.id}`);
    } catch (err) {
      setNotice(quotaMessage(err));
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
      <div className="mb-6 flex items-center gap-2">
        <ScaleIcon className="h-6 w-6 text-primary-500" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Mock Trials</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Practice courtroom advocacy against an AI bench and opposing counsel. Educational practice only.
          </p>
        </div>
      </div>

      {notice && (
        <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 p-3 rounded-md text-sm border border-amber-200 dark:border-amber-800">
          {notice}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Start new trial */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-5">
            <h2 className="text-base font-medium text-gray-900 dark:text-white mb-4">Start a new trial</h2>
            <form onSubmit={handleStart} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Topic</label>
                <select
                  value={form.topic_id}
                  onChange={(e) => setForm((f) => ({ ...f, topic_id: e.target.value }))}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a topic…</option>
                  {topics.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Your role</label>
                <select
                  value={form.student_role}
                  onChange={(e) => setForm((f) => ({ ...f, student_role: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 capitalize"
                >
                  {STUDENT_ROLES.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
                </select>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={form.with_witness}
                  onChange={(e) => setForm((f) => ({ ...f, with_witness: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Include a witness to examine</span>
              </label>
              <button
                type="submit"
                disabled={starting || !form.topic_id}
                className="w-full inline-flex justify-center items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {starting ? 'Starting…' : 'Start trial'}
              </button>
            </form>
          </div>
        </div>

        {/* Existing trials */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-base font-medium text-gray-900 dark:text-white">Your trials</h2>
            </div>
            {loading ? (
              <div className="py-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500" />
              </div>
            ) : trials.length === 0 ? (
              <p className="px-5 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                No trials yet. Start one to begin practising.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {trials.map((t) => (
                  <li key={t.id} className="px-5 py-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/student/mock-trials/${t.id}`} className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline truncate block">
                        {t.title || t.topic_name}
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {t.topic_name}{t.student_role ? ` · as ${t.student_role}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {typeof t.final_score === 'number' && (
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t.final_score}</span>
                      )}
                      <StatusBadge status={t.status} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockTrialLobby;
